import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounceCallback } from 'usehooks-ts';
import { CutawayParameters, ReleaseLocation, PredictionGeojson } from '../types/app.types';
import { openmeteoFetch } from '../weather_openmeteo';
import { predictFromWeather, predictionToGeojson } from '../predict_canopy';
import { isPointInWater } from '../point_in_water';

interface CanopyPredictionHookProps {
  sendMessage: (message: any) => void;
}

export const useCanopyPrediction = ({ sendMessage }: CanopyPredictionHookProps) => {
  const [cutawayParameters, setCutawayParameters] = useState<CutawayParameters>({});
  const [predictionGeojson, setPredictionGeojson] = useState<PredictionGeojson>({});
  const [releaseLocation, setReleaseLocation] = useState<ReleaseLocation>({ lat: 0, lng: 0 });
  const [skipWeatherQuery, setSkipWeatherQuery] = useState(false);
  const [userInitiatedChange, setUserInitiatedChange] = useState(false);
  
  const latestPredictionRef = useRef<{ params: any; weather: any; geojson: any }>({ 
    params: null, 
    weather: null, 
    geojson: null 
  });
  
  // Refs for stable access to latest values without breaking debounce
  const sendMessageRef = useRef(sendMessage);
  const skipWeatherQueryRef = useRef(skipWeatherQuery);
  
  // Update refs when values change
  React.useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);
  
  React.useEffect(() => {
    skipWeatherQueryRef.current = skipWeatherQuery;
  }, [skipWeatherQuery]);

  // Define the query key based on cutawayParameters
  const queryKey = [
    'weather',
    cutawayParameters.releaseLocation?.lat,
    cutawayParameters.releaseLocation?.lng,
    cutawayParameters.cutawayDate,
  ];

  // Weather data query
  const { data: weatherData } = useQuery({
    queryKey: queryKey,
    queryFn: () => {
      if (cutawayParameters.releaseLocation && cutawayParameters.cutawayDate) {
        const { lat, lng } = cutawayParameters.releaseLocation;
        return openmeteoFetch(lat, lng, cutawayParameters.cutawayDate);
      }
      return Promise.reject('Missing parameters');
    },
    enabled: !skipWeatherQuery,
    staleTime: 15 * 60 * 1000,
    retry: 1
  });

  // Water detection for prediction points
  const updateGeojsonForWater = useCallback(async (input_geojson: any) => {
    if (!input_geojson || Object.keys(input_geojson).length === 0) {
      return;
    }

    const geojson = structuredClone(input_geojson);
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    let modified = false;
    let already_called = false;

    for (let i = 0; i < geojson.features.length; ++i) {
      const feature = geojson.features[i];

      if (feature.geometry.type === "Point" && feature.properties.type === "prediction_landing") {
        if (already_called) {
          await sleep(2000); // Rate limiting
        }
        
        const [lon, lat] = feature.geometry.coordinates;
        const water_info = await isPointInWater(lat, lon);
        already_called = true;
        
        if (water_info.error === false && water_info.isWater === true) {
          feature.properties.water = true;
          feature.properties.type = "prediction_landing_water";
          modified = true;
        } else if (water_info.error) {
          break; // Stop on error to keep API happy
        }
      }
    }

    if (modified) {
      setPredictionGeojson(geojson);
    }
  }, []);

  const updateGeojsonForWaterDebounced = useCallback(
    useDebounceCallback(updateGeojsonForWater, 2000), 
    [updateGeojsonForWater]
  );

  // Send prediction to room (stable function using refs)
  const sendPredictionToRoom = useCallback(() => {
    const { params, weather, geojson } = latestPredictionRef.current;
    
    if (params && weather && geojson && !skipWeatherQueryRef.current) {
      sendMessageRef.current({
        type: 'update_canopy_prediction',
        cutawayParameters: params,
        weatherData: weather,
        predictionGeojson: geojson
      });
    }
  }, []); // No dependencies - function is stable and uses refs for latest values

  const sendPredictionToRoomDebounced = useCallback(
    useDebounceCallback(sendPredictionToRoom, 1000),
    [] // Empty dependencies to keep debounced function stable
  );

  const sendCanopyPrediction = useCallback((cutawayParams: any, weather: any, geojson: any) => {
    latestPredictionRef.current = { params: cutawayParams, weather, geojson };
    sendPredictionToRoomDebounced();
  }, [sendPredictionToRoomDebounced]);

  // Process weather data and generate predictions
  useEffect(() => {
    if (userInitiatedChange && !skipWeatherQuery && weatherData && cutawayParameters) {
      if (cutawayParameters.releaseLocation) {
        const { cutawayHeight, cutawaySinkRate, cutawayDate } = cutawayParameters;
        
        if (!cutawayHeight || !cutawaySinkRate || !cutawayDate) {
          return;
        }
        
        const predictions = predictFromWeather(weatherData, cutawayDate, cutawaySinkRate, cutawayHeight);
        const geojson = predictionToGeojson(predictions, cutawayParameters);

        updateGeojsonForWaterDebounced(structuredClone(geojson));
        setPredictionGeojson(geojson);
        sendCanopyPrediction(cutawayParameters, weatherData, geojson);
        setUserInitiatedChange(false);
      }
    }
  }, [weatherData, cutawayParameters, sendCanopyPrediction, skipWeatherQuery, userInitiatedChange, updateGeojsonForWaterDebounced]);

  // Handle parameter changes
  const handleParametersChanged = useCallback((params: any) => {
    setUserInitiatedChange(true);
    const combinedParams = { ...cutawayParameters, ...params };
    setCutawayParameters(combinedParams);
  }, [cutawayParameters]);

  const handleNewReleaseLocationSet = useCallback((latLng: ReleaseLocation) => {
    setReleaseLocation(latLng);
    setUserInitiatedChange(true);
    const combinedParams = { ...cutawayParameters, releaseLocation: latLng };
    setCutawayParameters(combinedParams);
  }, [cutawayParameters]);

  // Handle canopy prediction from other users
  const handleCanopyPredictionMessage = useCallback((message: any) => {
    setSkipWeatherQuery(true);
    setCutawayParameters(message.cutawayParameters);
    setPredictionGeojson(message.predictionGeojson);
    
    if (message.cutawayParameters.releaseLocation) {
      setReleaseLocation(message.cutawayParameters.releaseLocation);
    }
    
    setTimeout(() => {
      setSkipWeatherQuery(false);
    }, 1000);
  }, []);

  return {
    cutawayParameters,
    predictionGeojson,
    releaseLocation,
    handleParametersChanged,
    handleNewReleaseLocationSet,
    handleCanopyPredictionMessage
  };
};